import { toast } from "react-hot-toast";
import { useState, useEffect, useRef } from "react";
import { BeatLoader } from "react-spinners";
import MessageList from "./message/MessageList";
import { IoIosArrowDown } from "react-icons/io";
import { Button } from "@material-tailwind/react";
import { useSocket } from "../context/SocketContext";
import { useDispatch, useSelector } from "react-redux";
import MessageGroupList from "./message/MessageGroupList";
import { useNavigate, useLocation } from "react-router-dom";
import { IoSearchOutline } from "react-icons/io5";
import { accountBlockUser } from "../redux/ApiSlice/AccountProfileSlice";
import { totalUnreadGroupMessages } from "../redux/ApiSlice/MessageGroupSlice";
import { deleteChatRoom, reportMessage, searchMessages } from "../redux/ApiSlice/MessagesSlice";

import { CirclePlus, UserPlus } from 'lucide-react';

const Messages = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [search, setSearch] = useState("");
    const searchTimerRef = useRef(null);
    const searchRequestRef = useRef(null);
    const { unreadCount, unreadRequestCount } = useSocket();
    const { totalGroupUnreadCount } = useSelector((state) => state.messageGroup);
    const { searchResults, searchLoading } = useSelector((state) => state.messages);
    const [activeTab, setActiveTab] = useState(() => location.state?.activeTab || "Recent");
    // Modal states for MessageList
    const [reportUserId, setReportUserId] = useState(null);
    const [reportChatRoomId, setReportChatRoomId] = useState(null);

    // Custom modal states
    const [showReportUserModal, setShowReportUserModal] = useState(false);
    const [userReportReason, setUserReportReason] = useState("");
    const [userReportDetails, setUserReportDetails] = useState("");
    const [userReportLoading, setUserReportLoading] = useState(false);
    const [showUserReasonDropdown, setShowUserReasonDropdown] = useState(false);

    // Block user confirmation state
    const [showBlockConfirm, setShowBlockConfirm] = useState(false);
    const [blockUserId, setBlockUserId] = useState(null);
    const [blockUserName, setBlockUserName] = useState("");

    // Delete chat confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteChatRoomId, setDeleteChatRoomId] = useState(null);
    const [deleteChatUserName, setDeleteChatUserName] = useState("");

    useEffect(() => {
        if (location.state?.activeTab) {
            setActiveTab(location.state.activeTab);
        }
    }, [location.state]);

    useEffect(() => {
        dispatch(totalUnreadGroupMessages());
    }, [dispatch]);

    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        if (search.trim()) {
            searchTimerRef.current = setTimeout(() => {
                if (searchRequestRef.current) searchRequestRef.current.abort();
                searchRequestRef.current = dispatch(searchMessages(search.trim()));
            }, 400);
        }
        return () => clearTimeout(searchTimerRef.current);
    }, [search, dispatch]);

    // Report user functions
    const reportReasons = [
        "Harassment",
        "Inappropriate",
        "Bullying or targeted harassment",
        "Hate speech",
        "Blackmail",
        "Sexual Harassment",
        "Impersonation/Fake account",
        "Graphic Violence/Gore",
        "Spam",
        "Drugs/Weapons",
        "Sexual content involving suspected minors",
        "Stalking or Intimidation",
        "Child safety concerns",
        "Self-harm or suicide content",
        "Violence or physical harm threats",
        "Dangerous acts & challenges",
        "Human exploitation/trafficking",
        "Scams/Fraud/Phishing",
        "Illegal Services/Solicitation",
        "IP Infringement",
        "Other"
    ];

    const handleUserReasonSelect = (selectedReason) => {
        setUserReportReason(selectedReason);
        setShowUserReasonDropdown(false);
    };

    const handleSubmitUserReport = async () => {
        if (!userReportReason) {
            toast.error("Please select a reason for reporting the user.");
            return;
        }

        try {
            setUserReportLoading(true);
            const data = {
                reported_user_id: reportUserId,
                chat_room_id: reportChatRoomId,
                reason: userReportReason,
                details: userReportDetails
            };

            const result = await dispatch(reportMessage(data));

            if (result.meta?.requestStatus === "rejected") {
                const errMsg = result.payload?.non_field_errors?.[0] || "Error reporting user";
                toast.error(errMsg);
            } else {
                toast.success("User reported successfully.");
                setShowReportUserModal(false);
                setUserReportReason("");
                setUserReportDetails("");
                setReportUserId(null);
                setReportChatRoomId(null);
            }
        } catch (error) {
            toast.error("Error reporting user");
            console.error("Error reporting user:", error);
        } finally {
            setUserReportLoading(false);
        }
    };

    // Block user functions
    const handleBlockConfirm = async () => {
        if (blockUserId) {
            const result = await dispatch(accountBlockUser(blockUserId));
            if (result.meta?.requestStatus === "rejected") {
                toast.error("Error blocking user");
                return;
            }
            setShowBlockConfirm(false);
            setBlockUserId(null);
            setBlockUserName("");
            toast.success("User blocked successfully");
        }
    };

    const handleBlockCancel = () => {
        setShowBlockConfirm(false);
        setBlockUserId(null);
        setBlockUserName("");
    };

    // Delete chat functions
    const handleDeleteConfirm = async () => {
        if (deleteChatRoomId) {
            const result = await dispatch(deleteChatRoom(deleteChatRoomId));
            if (result.meta?.requestStatus === "rejected") {
                toast.error("Failed to delete chat. Please try again.");
                return;
            }
            setShowDeleteConfirm(false);
            setDeleteChatRoomId(null);
            setDeleteChatUserName("");
            toast.success("Chat deleted successfully");
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
        setDeleteChatRoomId(null);
        setDeleteChatUserName("");
    };

    // Modal handler functions to pass to MessageList
    const handleReportUser = (userId, chatRoomId) => {
        setReportUserId(userId);
        setReportChatRoomId(chatRoomId);
        setShowReportUserModal(true);
    };

    const handleBlockUser = (userId, userName) => {
        setBlockUserId(userId);
        setBlockUserName(userName);
        setShowBlockConfirm(true);
    };

    const handleDeleteChat = (chatRoomId, userName) => {
        setDeleteChatRoomId(chatRoomId);
        setDeleteChatUserName(userName);
        setShowDeleteConfirm(true);
    };

    return (
        <div className="relative h-full bg-white dark:bg-[#1E1D21] overflow-hidden">
            <div className="relative h-12 bg-[#BDE6FF] rounded-b-xl flex items-center justify-center">
                <p className="font-bold text-[18px] text-black dark:text-black">
                    Messages
                </p>
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-x-2">
                    <CirclePlus
                        size={18}
                        onClick={() => navigate("/message/create")}
                        className="mt-[1px] text-black dark:text-black cursor-pointer"
                    />
                    <div className="relative">
                        <UserPlus
                            size={20}
                            onClick={() => navigate("/message/request")}
                            className="text-black dark:text-black cursor-pointer"
                        />
                        {unreadRequestCount?.data?.total_unread_messages > 0 && (
                            <span className="absolute -top-2 -right-1 bg-red-600 text-white text-[9px] min-w-[16px] h-[16px] flex items-center justify-center rounded-full px-1 shadow">
                                {unreadRequestCount.data.total_unread_messages}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="w-full flex justify-center mt-2">
                <div className="w-full mx-3 relative">
                    <IoSearchOutline className="absolute cursor-pointer top-2 right-3 text-[18px] text-black dark:text-white" />
                    <input
                        type="text"
                        value={search}
                        placeholder="Search messages"
                        onChange={(e) => setSearch(e.target.value)}
                        className="rounded-full text-[14px] w-full py-1.5 pr-8 pl-3 bg-gray-100 dark:bg-[#333333] text-black dark:text-white border border-gray-900 outline-none"
                    />
                </div>
            </div>

            {/* <div className="grid grid-cols-12 gap-x-[1px] mt-1">
                <button
                    onClick={() => {
                        setActiveTab("Recent");
                        setSearch("");
                    }}
                    className={`col-span-6 focus:border-none text-[16px] uppercase p-[7px] rounded-none 
                        ${activeTab === "Recent" ? "bg-blue-600 text-center text-white" :
                            "bg-black dark:bg-gray-800 text-white"}`
                    }
                >
                    <p className="text-[12px] md:text-[10px] font-semibold">
                        Recent {unreadCount?.data?.total_unread_messages > 0 && `(${unreadCount?.data?.total_unread_messages})`}
                    </p>
                </button>
                <button
                    onClick={() => {
                        setActiveTab("Groups");
                        setSearch("");
                    }}
                    className={`col-span-6 focus:border-none text-[16px] uppercase p-[7px] rounded-none 
                        ${activeTab === "Groups" ? "bg-blue-600 text-center text-white" :
                            "bg-black dark:bg-gray-800 text-white"}`
                    }
                >
                    <p className="text-[12px] md:text-[10px] font-semibold">
                        Group Chats {totalGroupUnreadCount?.total_unread_group_messages > 0 && `(${totalGroupUnreadCount?.total_unread_group_messages})`}
                    </p>
                </button>
            </div> */}

            <div className="absolute top-[102px] bottom-0 w-full overflow-y-scroll no-scrollbar">
                {activeTab === "Recent" && (
                    <MessageList
                        searchQuery={search}
                        searchResults={searchResults}
                        searchLoading={searchLoading}
                        onReportUser={handleReportUser}
                        onBlockUser={handleBlockUser}
                        onDeleteChat={handleDeleteChat}
                    />
                )}
                {activeTab === "Groups" && <MessageGroupList searchQuery={search} />}
            </div>

            {/* Report User Modal */}
            {showReportUserModal && (
                <div className="absolute inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
                    <div className="bg-white h-auto py-2 dark:bg-gray-900 shadow-lg rounded-lg w-[300px] flex flex-col items-center justify-center">
                        <p className="text-[24px] font-bold text-black dark:text-white text-center">
                            Report User
                        </p>
                        <p className="text-[12px] text-black mb-2 dark:text-white text-center">
                            Are you sure you want to report this user?
                        </p>
                        <div className="px-2 w-full">
                            <label className="block text-[12px] mb-0.5 font-semibold text-black dark:text-white">
                                Reason <span className="text-red-500">*</span>
                            </label>
                            <div className="relative user-custom-dropdown">
                                <div
                                    onClick={() => setShowUserReasonDropdown(!showUserReasonDropdown)}
                                    className="w-full h-[35px] border border-gray-400 dark:border-gray-600 bg-white dark:bg-black rounded-lg p-1 text-[12px] focus:outline-none cursor-pointer flex items-center justify-between"
                                >
                                    <span className={userReportReason ? "text-black dark:text-white" : "text-gray-500"}>
                                        {userReportReason || "Select a reason"}
                                    </span>
                                    <IoIosArrowDown className="text-gray-500" />
                                </div>
                                {showUserReasonDropdown && (
                                    <div className="absolute top-[36px] left-0 w-full bg-white dark:bg-black border border-gray-400 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-[125px] overflow-y-auto">
                                        {reportReasons.map((reasonOption, index) => (
                                            <div
                                                key={index}
                                                onClick={() => handleUserReasonSelect(reasonOption)}
                                                className="px-2 py-1.5 text-[12px] text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                                            >
                                                {reasonOption}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="px-2 w-full mt-2">
                            <label htmlFor="user-report-details" className="block text-[12px] mb-0.5 font-semibold text-black dark:text-white">
                                Optional Details
                            </label>
                            <textarea
                                value={userReportDetails}
                                maxLength={200}
                                id="user-report-details"
                                placeholder="Type your report details here..."
                                onChange={(e) => setUserReportDetails(e.target.value)}
                                className="w-full h-[50px] border border-gray-400 dark:border-gray-600 dark:bg-black rounded-lg p-1.5 text-[12px] focus:outline-none resize-none"
                            />
                        </div>

                        <div className="flex gap-2 my-2">
                            <Button
                                onClick={() => {
                                    setShowReportUserModal(false);
                                    setUserReportReason("");
                                    setUserReportDetails("");
                                    setReportUserId(null);
                                    setReportChatRoomId(null);
                                }}
                                size="sm"
                                className="bg-[#1b1b1b] dark:bg-gray-400 dark:text-black dark:hover:bg-gray-500 hover:bg-[#0f0f0f] text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmitUserReport}
                                size="sm"
                                className="border border-gray-400 dark:border-gray-800 bg-red-900 hover:bg-red-600 text-white"
                            >
                                {userReportLoading ? <BeatLoader
                                    size={10}
                                    color={"#fff"}
                                    loading={userReportLoading}
                                /> : "Report"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Block User Confirmation Modal */}
            {showBlockConfirm && (
                <div className="absolute inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="bg-white h-auto py-4 dark:bg-gray-900 shadow-lg rounded-lg w-[300px] flex flex-col items-center justify-center">
                        <p className="text-[24px] font-bold text-black dark:text-white text-center mb-2">
                            Block User
                        </p>
                        <p className="text-[12px] text-black px-4 dark:text-white text-center">
                            Are you sure you want to block {blockUserName}?
                        </p>
                        <div className="flex gap-2 mt-4">
                            <Button
                                onClick={handleBlockCancel}
                                size="sm"
                                className="bg-[#1b1b1b] dark:bg-gray-400 dark:text-black dark:hover:bg-gray-500 hover:bg-[#0f0f0f] text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleBlockConfirm}
                                size="sm"
                                className="bg-red-900 hover:bg-red-600 text-white"
                            >
                                Block
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Chat Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="absolute inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="bg-white h-auto py-4 dark:bg-gray-900 shadow-lg rounded-lg w-[300px] flex flex-col items-center justify-center">
                        <p className="text-[24px] font-bold text-black dark:text-white text-center mb-2">
                            Delete Chat
                        </p>
                        <p className="text-[12px] text-black px-4 dark:text-white text-center">
                            Are you sure you want to delete this chat with {deleteChatUserName}? This action cannot be undone.
                        </p>
                        <div className="flex gap-2 mt-4">
                            <Button
                                onClick={handleDeleteCancel}
                                size="sm"
                                className="bg-[#1b1b1b] dark:bg-gray-400 dark:text-black dark:hover:bg-gray-500 hover:bg-[#0f0f0f] text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleDeleteConfirm}
                                size="sm"
                                className="bg-red-900 hover:bg-red-600 text-white"
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Messages